/**
 * taiga-browser (Cursor webview) の iframe 内では sandbox に allow-modals が無く、
 * ネイティブの alert / confirm が silent no-op になる (confirm は常に false)。
 * 埋め込み時のみ自前の DOM ダイアログで代替し、通常ブラウザではネイティブに委譲する。
 */

const isEmbedded = () =>
	typeof window !== "undefined" && window.parent !== window

const showDialog = async (
	message: string,
	buttons: { label: string; value: boolean; primary?: boolean }[]
): Promise<boolean> =>
	await new Promise((resolve) => {
		const overlay = document.createElement("div")
		overlay.style.cssText =
			"position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,0.45);" +
			"display:flex;align-items:center;justify-content:center;"
		const box = document.createElement("div")
		box.style.cssText =
			"background:#fff;color:#222;border-radius:8px;padding:20px 24px;" +
			"max-width:420px;width:calc(100% - 48px);box-shadow:0 8px 30px rgba(0,0,0,0.25);" +
			"font-size:14px;line-height:1.6;white-space:pre-wrap;word-break:break-word;"
		box.textContent = message

		const row = document.createElement("div")
		row.style.cssText =
			"display:flex;justify-content:flex-end;gap:8px;margin-top:16px;"

		const cleanup = (value: boolean) => {
			document.removeEventListener("keydown", onKeyDown, true)
			overlay.remove()
			resolve(value)
		}
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault()
				e.stopPropagation()
				cleanup(false)
			}
			if (e.key === "Enter") {
				e.preventDefault()
				e.stopPropagation()
				cleanup(true)
			}
		}

		for (const b of buttons) {
			const btn = document.createElement("button")
			btn.textContent = b.label
			btn.style.cssText = b.primary
				? "background:#2563eb;color:#fff;border:none;border-radius:6px;" +
					"padding:6px 16px;font-size:13px;cursor:pointer;"
				: "background:#f3f4f6;color:#222;border:1px solid #d1d5db;border-radius:6px;" +
					"padding:6px 16px;font-size:13px;cursor:pointer;"
			btn.onclick = () => cleanup(b.value)
			row.appendChild(btn)
		}

		box.appendChild(row)
		overlay.appendChild(box)
		overlay.onclick = (e) => {
			if (e.target === overlay) cleanup(false)
		}
		document.addEventListener("keydown", onKeyDown, true)
		document.body.appendChild(overlay)
	})

// alert 代替。戻り値は使わない前提なので fire-and-forget。
export const appAlert = (message: string): void => {
	if (!isEmbedded()) {
		window.alert(message)
		return
	}
	void showDialog(message, [{ label: "OK", value: true, primary: true }])
}

// confirm 代替。ネイティブと違い async なので呼び出し側で await が必要。
export const appConfirm = async (message: string): Promise<boolean> => {
	if (!isEmbedded()) {
		return await Promise.resolve(window.confirm(message))
	}
	return await showDialog(message, [
		{ label: "キャンセル", value: false },
		{ label: "OK", value: true, primary: true },
	])
}
